import React, {useCallback, useEffect, useRef, useState} from "react";
import {getReviews, submitReview} from "../api";
import StarRating, {StarInput} from "./StarRating";
import "./ProductReviews.css";

const PAGE_SIZE = 5;
const BODY_MIN = 10;
const BODY_MAX = 2000;

const SORT_OPTIONS = [
  {value: "recent", label: "Most recent"},
  {value: "highest", label: "Highest rated"},
  {value: "lowest", label: "Lowest rated"},
];

const EMPTY_FORM = {rating: 0, title: "", body: "", name: "", email: ""};

function formatReviewDate(iso) {
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function ReviewForm({productId, onDone, onCancel}) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [hover, setHover] = useState(0);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState("");
  const formRef = useRef(null);

  // Only scroll when the shopper opened the form — an always-visible form
  // (no onCancel) must not yank the page down on load.
  useEffect(() => {
    if (onCancel)
      formRef.current?.scrollIntoView({behavior: "smooth", block: "center"});
  }, []);

  function set(field, value) {
    setForm((f) => ({...f, [field]: value}));
    if (errors[field]) setErrors((e) => ({...e, [field]: undefined}));
  }

  function validate() {
    const e = {};
    if (!form.rating) e.rating = "Please select a star rating";
    if (form.body.trim().length < BODY_MIN)
      e.body = `Tell us a little more (at least ${BODY_MIN} characters)`;
    // if (!form.name.trim()) e.name = "Please enter your name";
    // if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
    //   e.email = "Please enter a valid email";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setServerError("");
    if (!validate()) return;
    setSubmitting(true);
    try {
      const {data} = await submitReview({...form, product: productId});
      onDone(data);
    } catch (err) {
      setServerError(
        err.response?.data?.message ||
          "Something went wrong. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form className="rv-form" onSubmit={handleSubmit} ref={formRef} noValidate>
      <div className="rv-form-head">
        <h3>Write a review</h3>
        {onCancel && (
          <button
            type="button"
            className="rv-form-close"
            onClick={onCancel}
            aria-label="Close"
          >
            ×
          </button>
        )}
      </div>

      <div className="rv-field">
        <span className="rv-label">Overall rating *</span>
        <StarInput
          value={form.rating}
          onChange={(n) => set("rating", n)}
          hover={hover}
          onHover={setHover}
        />
        {errors.rating && <span className="rv-error">{errors.rating}</span>}
      </div>

      <label className="rv-field">
        <span className="rv-label">Review title</span>
        <input
          type="text"
          maxLength={100}
          placeholder="Sum it up in a few words"
          value={form.title}
          onChange={(e) => set("title", e.target.value)}
        />
      </label>

      <label className="rv-field">
        <span className="rv-label">Your review *</span>
        <textarea
          rows={5}
          maxLength={BODY_MAX}
          placeholder="How's the print quality, finish and size? How are you using it?"
          value={form.body}
          onChange={(e) => set("body", e.target.value)}
          aria-invalid={!!errors.body}
        />
        <span className="rv-field-foot">
          {errors.body ? (
            <span className="rv-error">{errors.body}</span>
          ) : (
            <span />
          )}
          <span className="rv-count">
            {form.body.length}/{BODY_MAX}
          </span>
        </span>
      </label>

      <div className="rv-form-row">
        <label className="rv-field">
          <span className="rv-label">Name *</span>
          <input
            type="text"
            maxLength={60}
            autoComplete="name"
            placeholder="Shown on your review"
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            aria-invalid={!!errors.name}
          />
          {errors.name && <span className="rv-error">{errors.name}</span>}
        </label>
        <label className="rv-field">
          <span className="rv-label">Email *</span>
          <input
            type="email"
            maxLength={120}
            autoComplete="email"
            placeholder="Never shown publicly"
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
            aria-invalid={!!errors.email}
          />
          {errors.email ? (
            <span className="rv-error">{errors.email}</span>
          ) : (
            <span className="rv-hint">
              Use your order email to get a Verified Buyer badge
            </span>
          )}
        </label>
      </div>

      {serverError && <p className="rv-server-error">{serverError}</p>}

      <div className="rv-form-actions">
        <button type="submit" className="btn-pill solid" disabled={submitting}>
          {submitting ? "Submitting…" : "Submit review"}
        </button>
        {onCancel && (
          <button type="button" className="btn-pill outline" onClick={onCancel}>
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}

function ReviewItem({review}) {
  const [expanded, setExpanded] = useState(false);
  const isLong = review.body.length > 320;
  const text =
    isLong && !expanded ? review.body.slice(0, 320).trim() + "…" : review.body;

  return (
    <article className="rv-item">
      <div className="rv-item-head">
        <span className="rv-avatar" aria-hidden="true">
          {review.name.trim().charAt(0).toUpperCase()}
        </span>
        <div className="rv-item-meta">
          <span className="rv-item-name">
            {review.name}
            {review.verified && (
              <span className="rv-verified">
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M20 6 9 17l-5-5" />
                </svg>
                Verified buyer
              </span>
            )}
          </span>
          {/* <time className="rv-item-date" dateTime={review.createdAt}>
            {formatReviewDate(review.createdAt)}
          </time> */}
        </div>
      </div>
      <StarRating value={review.rating} size={18} />
      {review.title && <h4 className="rv-item-title">{review.title}</h4>}
      <p className="rv-item-body">
        {text}
        {isLong && (
          <button
            type="button"
            className="rv-link"
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? "Show less" : "Read more"}
          </button>
        )}
      </p>
    </article>
  );
}

// openRequest: bump it (e.g. from the rating link under the product title)
// to scroll here with the form already open.
export default function ProductReviews({productId, openRequest = 0}) {
  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [sort, setSort] = useState("recent");
  const [ratingFilter, setRatingFilter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const sectionRef = useRef(null);

  const load = useCallback(
    (nextPage) => {
      const params = {
        product: productId,
        sort,
        page: nextPage,
        limit: PAGE_SIZE,
      };
      if (ratingFilter) params.rating = ratingFilter;
      return getReviews(params).then(({data}) => {
        setReviews((prev) =>
          nextPage === 1 ? data.reviews : [...prev, ...data.reviews],
        );
        setSummary(data.summary);
        setTotal(data.total);
        setPages(data.pages);
        setPage(nextPage);
      });
    },
    [productId, sort, ratingFilter],
  );

  useEffect(() => {
    setLoading(true);
    load(1)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [load]);

  const count = summary?.count || 0;
  const hasReviews = count > 0;

  useEffect(() => {
    if (!openRequest) return;
    setSubmitted(null);
    if (hasReviews && !formOpen)
      setFormOpen(true); // the form scrolls itself into view
    else
      sectionRef.current?.scrollIntoView({behavior: "smooth", block: "start"});
  }, [openRequest]);

  function loadMore() {
    setLoadingMore(true);
    load(page + 1)
      .catch(() => {})
      .finally(() => setLoadingMore(false));
  }

  function openForm() {
    setSubmitted(null);
    setFormOpen(true);
  }

  function handleSubmitted(result) {
    setFormOpen(false);
    setSubmitted(result);
    if (result.status === "approved") load(1).catch(() => {});
  }

  const successBanner = submitted && (
    <div className="rv-success" role="status">
      <strong>Thank you for your review!</strong>{" "}
      {submitted.status === "approved"
        ? ""
        : "It'll appear here once our team has taken a quick look."}
    </div>
  );

  // No reviews yet — skip the empty summary card and go straight to the form
  if (!loading && !hasReviews) {
    return (
      <section
        className="rv"
        id="reviews"
        aria-labelledby="rv-title"
        ref={sectionRef}
      >
        <div className="rv-header">
          <p className="rv-eyebrow">Customer Reviews</p>
          <h2 className="rv-title" id="rv-title">
            Be the first to review
          </h2>
          <p className="rv-sub">
            Bought this piece? Your review helps other shoppers choose with
            confidence.
          </p>
        </div>
        <div className="rv-solo">
          {successBanner || (
            <ReviewForm productId={productId} onDone={handleSubmitted} />
          )}
        </div>
      </section>
    );
  }

  return (
    <section
      className="rv"
      id="reviews"
      aria-labelledby="rv-title"
      ref={sectionRef}
    >
      <div className="rv-header">
        <p className="rv-eyebrow">Customer Reviews</p>
        <h2 className="rv-title" id="rv-title">
          What our customers say
        </h2>
      </div>

      <div className="rv-layout">
        {/* Summary */}
        <aside className="rv-summary">
          {hasReviews && (
            <>
              <div className="rv-score">
                <span className="rv-score-value">
                  {summary.average.toFixed(1)}
                </span>
                <div>
                  <StarRating value={summary.average} size={20} />
                  <p className="rv-score-count">
                    Based on {count} review{count === 1 ? "" : "s"}
                  </p>
                </div>
              </div>

              <ul className="rv-bars">
                {[5, 4, 3, 2, 1].map((n) => {
                  const c = summary.distribution[n] || 0;
                  const pct = count ? Math.round((c / count) * 100) : 0;
                  const active = ratingFilter === n;
                  return (
                    <li key={n}>
                      <button
                        type="button"
                        className={`rv-bar-row ${active ? "active" : ""}`}
                        onClick={() => setRatingFilter(active ? null : n)}
                        disabled={c === 0}
                        aria-pressed={active}
                        aria-label={`${n} star: ${c} review${c === 1 ? "" : "s"}. ${active ? "Clear filter" : "Show only these"}`}
                      >
                        <span className="rv-bar-label">{n} ★</span>
                        <span className="rv-bar-track">
                          <span
                            className="rv-bar-fill"
                            style={{width: `${pct}%`}}
                          />
                        </span>
                        <span className="rv-bar-count">{c}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </>
          )}

          <button
            type="button"
            className="btn-pill outline rv-write-btn"
            onClick={openForm}
            disabled={formOpen}
          >
            Write a review
          </button>
        </aside>

        {/* List */}
        <div className="rv-main">
          {successBanner}

          {formOpen && (
            <ReviewForm
              productId={productId}
              onDone={handleSubmitted}
              onCancel={() => setFormOpen(false)}
            />
          )}

          {hasReviews && (
            <div className="rv-toolbar">
              <div className="rv-toolbar-left">
                <span className="rv-showing">
                  {total} review{total === 1 ? "" : "s"}
                </span>
                {ratingFilter && (
                  <button
                    type="button"
                    className="rv-chip"
                    onClick={() => setRatingFilter(null)}
                  >
                    {ratingFilter} star only <span aria-hidden="true">×</span>
                  </button>
                )}
              </div>
              <label className="rv-sort">
                <span>Sort by</span>
                <select value={sort} onChange={(e) => setSort(e.target.value)}>
                  {SORT_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}

          {loading ? (
            <div className="rv-skeletons">
              {[0, 1].map((i) => (
                <div key={i} className="rv-skeleton" />
              ))}
            </div>
          ) : (
            reviews.length > 0 && (
              <div className="rv-list">
                {reviews.map((r) => (
                  <ReviewItem key={r._id} review={r} />
                ))}
              </div>
            )
          )}

          {!loading && page < pages && (
            <button
              type="button"
              className="btn-pill outline rv-more"
              onClick={loadMore}
              disabled={loadingMore}
            >
              {loadingMore ? "Loading…" : `Show more reviews`}
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
